'use client';

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axiosInstance from "@/lib/axios";
import { shouldShowModal, markModalAsShown } from '@/lib/tutorial utilities/modal';
import { ModalId, TutorialContent } from '@/lib/types/tutorial';

interface UseTutorialModalReturn {
  showModal: boolean;
  modalContent: TutorialContent | null;
  checkAndShowModal: (onProceedCallback?: () => void) => boolean;
  handleProceed: (callback?: () => void) => Promise<void>;
  handleClose: (callback?: () => void) => Promise<void>;
}

export function useTutorialModal(
  modalId: ModalId, 
  userId?: string, 
  videoId?: number
): UseTutorialModalReturn {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<TutorialContent | null>(null);
  const queryClient = useQueryClient();

  const syncWithBackend = async () => {
    if (!videoId) return;
    try {
      await axiosInstance.patch(
        `/user/auth/videos/${videoId}/watched`, 
        {}, 
        { baseURL: "/api/proxy" } 
      );
      
      // Refresh the user query 
      queryClient.invalidateQueries({ queryKey: ["user"] });
    } catch (error) {
      console.error(`Failed to sync video ${videoId}:`, error);
    }
  };

  const checkAndShowModal = (onProceedCallback?: () => void): boolean => {
    const { shouldShow, content } = shouldShowModal(modalId, userId);
    
    if (shouldShow && content) {
      setModalContent(content);
      setShowModal(true);
      return false; 
    } else {
      if (onProceedCallback) onProceedCallback();
      return true; 
    }
  };

  const handleProceed = async (callback?: () => void) => {
    markModalAsShown(modalId, userId);
    if (videoId) await syncWithBackend();
    
    setShowModal(false);
    setModalContent(null);
    if (callback) callback();
  };

  const handleClose = async (callback?: () => void) => {
    markModalAsShown(modalId, userId);
    if (videoId) await syncWithBackend();

    setShowModal(false);
    setModalContent(null);
    if (callback) callback();
  };

  return {
    showModal,
    modalContent,
    checkAndShowModal,
    handleProceed,
    handleClose
  };
}